import { useMemo } from 'react'
import { FC, useEffect } from 'react'
import { classes, stylesheet } from 'typestyle'
import { Link, useLocation } from 'react-router-dom'

/* Common ======================================================================================= */
import { get } from '../../__aeki__/share/find'

/* Hooks ======================================================================================== */
import useFetch from '../../__aeki__/hooks/use-fetch'
import useBoolean from '../../__aeki__/hooks/use-boolean'

/* Components =================================================================================== */
import AppLayout, { MAX_WIDTH } from './layout'
import Footer from '../__home__/components/footer'
import Skeleton from '../../__aeki__/components/skeleton'
import { BlogPostComponent } from './components/blog-post'
import { Box } from '../../__aeki__/components/box/box.component'
import { Button } from '../../__aeki__/components/button'
import { Heading } from '../../__aeki__/components/text/heading.component'
import { Line } from '../../__aeki__/components/line'
import { Loader } from '../../__aeki__/components/loader'
import { Text } from '../../__aeki__/components/text/text.component'
import { Title } from '../../__aeki__/components/text/title.component'
import { VisibleFade } from '../__home__/components/visible/visible-fade'
import { VisibleOpacity } from '../__home__/components/visible/visible-opacity'

/* Styles ======================================================================================= */
import { createMediaQuery } from '../../__aeki__/components/style-class/style-class.utils'
import { mb40 } from '../../__aeki__/styles/styleset/margin/mb40'
import { paddingVerticalOuter } from '../../__aeki__/styles/default/padding'
import { textCenter } from '../../__aeki__/styles/styleset/text-center/textCenter'
import { mb24 } from '../../__aeki__/styles/styleset/margin/mb24'
import { mb8 } from '../../__aeki__/styles/styleset/margin/mb8'
import { pb40 } from '../../__aeki__/styles/styleset/padding/pb40'
import { pt40 } from '../../__aeki__/styles/styleset/padding/pt40'
import { mt8 } from '../../__aeki__/styles/styleset/margin/mt8'
import { mt52 } from '../../__aeki__/styles/styleset/margin/mt52'
import { A } from '../../__aeki__/components/a'
import { Info } from '../posts/components/info'
import { mb12 } from '../../__aeki__/styles/styleset/margin/mb12'

const BlogPost: FC<any> = (props: any) => {
  const { inject, eject } = props
  const injection = inject()
  const historyLocation = useLocation()
  const url = `${process.env.REACT_APP_API_URL}${historyLocation.pathname}`
  const [, $isNotFound] = useBoolean(false)

  const [post, $post] = useFetch(
    Object.keys(injection).length
      ? injection
      : {
          body: {
            allowBookmarks: false,
            body: [],
          },
        },
  )

  useEffect(() => {
    return () => {
      eject()
    }
  }, [])

  useEffect(() => {
    if (post.mounted && Object.keys(injection).length === 0) {
      $post.get({ url }).catch(() => {
        $isNotFound.set(true)
      })
    }
  }, [post.mounted])

  function renderDate() {
    const updatedAt = get('createdAt', post.payload, 'string', '')
    const createdAt = get('createdAt', post.payload, 'string', '')

    if (updatedAt) {
      return (
        <Text size="sm" baseline="surface">
          Updated at{' '}
          {new Date(updatedAt).toLocaleDateString(undefined, {
            hour: 'numeric',
            minute: 'numeric',
            second: 'numeric',
            hour12: true,
          })}
        </Text>
      )
    }

    if (createdAt) {
      return (
        <Text size="sm" baseline="surface">
          Created at{' '}
          {new Date(createdAt).toLocaleDateString(undefined, {
            hour: 'numeric',
            minute: 'numeric',
            second: 'numeric',
            hour12: true,
          })}
        </Text>
      )
    }
  }

  const promotion = useMemo(
    () => (
      <Box baseline="background" className={classes(paddingVerticalOuter, pt40, pb40)}>
        <VisibleFade>
          <Box baseline="none" maxWidth={MAX_WIDTH} centerAutoX row alignCenterX>
            <Heading size="xs" className={classes(mb12, textCenter)} semiBold crank>
              Did you find this post helpful?
            </Heading>
            <Box className={mb24} baseline="none">
              <Heading size="sm">There are more to read</Heading>
            </Box>
            <Link to={'/a/docs'}>
              <Button label="Go to Documentation" baseline="primary" size="lg" square />
            </Link>
          </Box>
        </VisibleFade>
      </Box>
    ),
    [],
  )

  const parentName = get('body.parent.body.name', post.payload, 'string')
  const title = get('body.name', post.payload, 'string')
  const summary = get('body.summary', post.payload, 'string')
  const mainImage = get(
    'body.mainImage.0.file.body.location',
    post.payload,
    'string',
    `${process.env.REACT_APP_WEB_URL}/images/blog-default.jpg`,
  )
  const minutes = get('body.minutes', post.payload, 'string')
  const author = get('body.author.body', post.payload, 'object')

  const head = useMemo(() => {
    return {
      title: `${title} - ChildAt.io`,
      description: summary,
      mainImage,
    }
  }, [title !== '-'])

  const content = useMemo(() => {
    return (
      <>
        <Box baseline="surface">
          {post.payload?.body?.body?.length > 0 && (
            <BlogPostComponent post={{ payload: post.payload }} />
          )}
          <Line className={mt52} />
        </Box>
      </>
    )
  }, [post.payload?.body?.body?.length > 0])

  return (
    <AppLayout.render
      head={head}
      headerProps={{
        maxWidth: MAX_WIDTH,
        scrollEffect: true,
        allowToggleEffect: true,
      }}
      content={
        <>
          <Box baseline="secondary" className={STYLES.containerHero} row>
            <Box
              baseline="none"
              className={STYLES.containerHeroContent}
              flex
              alignCenterX
              alignCenterY
            >
              <Box
                className={classes(paddingVerticalOuter)}
                baseline="none"
                maxWidth={MAX_WIDTH}
                centerAutoX
                fullWidth
                row
              >
                <VisibleOpacity>
                  <Box row baseline="none" alignCenterX>
                    <Link to={'/a/docs'}>
                      <Text baseline="primary" className={mb8} size="lg" tag={'h2'} link>
                        Documentation
                      </Text>
                    </Link>
                    <Loader
                      loaded={title !== '-'}
                      skeleton={<Skeleton width={'50%'} height={70.88} className={mt8} />}
                      component={
                        <Title className={textCenter} size="md" semiBold tag={'h1'} crank>
                          {title}
                        </Title>
                      }
                    />
                  </Box>
                </VisibleOpacity>
              </Box>
            </Box>
          </Box>
          <Info maxWidth={MAX_WIDTH} label="Subscribe" labelWidth={80} icon="error">
            <Text size="sm">
              Sign up with us so you can get notified of our posts by clicking{' '}
              <A to={'/a/signin'}>
                <Text size="sm" link>
                  here!
                </Text>
              </A>
            </Text>
          </Info>
          {/* <Info
            maxWidth={MAX_WIDTH}
            label="Info"
            labelWidth={80}
            icon="description"
            disableUpperLine
          >
            <Loader
              loaded={post.payload?.body?.body?.length > 0}
              skeleton={
                <Box row flex>
                  <Skeleton width={200} height={14} />
                </Box>
              }
              component={
                <>
                  {summary !== '-' && summary !== '' && (
                    <RichTextView size="sm" __html={summary} scroll={false} disablePadding />
                  )}
                  <Box
                    baseline="none"
                    className={classes(
                      summary !== '-' && summary !== '' ? mt16 : '',
                      lh30,
                      hidemdi,
                    )}
                    flex
                    alignCenterY
                  >
                    {minutes !== '-' && (
                      <>
                        <Text>{minutes} min read</Text>
                        <i className={classes('material-icons', f4i, ml4, mr4)}>circle</i>
                      </>
                    )}
                    {author.fullName && (
                      <>
                        <Text>Written by {author.fullName}</Text>
                        <i className={classes('material-icons', f4i, ml4, mr4)}>circle</i>
                      </>
                    )}
                    <Text>{renderDate()}</Text>
                  </Box>
                  <Box
                    baseline="none"
                    className={classes(
                      summary !== '-' && summary !== '' ? mt16 : '',
                      lh30,
                      hide,
                      flexmd,
                    )}
                    row
                  >
                    {minutes !== '-' && (
                      <>
                        <Text className={classes(mb4)}>{minutes} min read</Text>
                      </>
                    )}
                    {author.fullName && (
                      <>
                        <Text className={classes(mb4)}>Written by {author.fullName}</Text>
                      </>
                    )}
                    <Text className={classes(mb4)}>{renderDate()}</Text>
                  </Box>
                </>
              }
            />
          </Info> */}
          <Loader
            loaded={title !== '-'}
            skeleton={
              <Box className={pt40} minHeight={200} row flex>
                <Box
                  className={classes(paddingVerticalOuter)}
                  baseline="none"
                  maxWidth={MAX_WIDTH}
                  centerAutoX
                  fullWidth
                  row
                >
                  <Skeleton width="100%" height={32} />
                </Box>
              </Box>
            }
            component={content}
          />
          {promotion}
          <Line className={mb40} />
          <Footer />
        </>
      }
    />
  )
}

const STYLES = stylesheet({
  containerHero: {
    marginTop: -56,
    paddingTop: 56,
  },
  containerHeroContent: {
    height: 'calc(40vh)',
    minHeight: 320,
    ...createMediaQuery.sm({
      minHeight: 'calc(100vh - 97px)',
    }),
  },
  lineHero: {
    opacity: 0.1,
  },
})

export default BlogPost
